import { Badge } from '@/components/shadcn/badge'
import { ColumnDef, Row } from '@tanstack/react-table'
import { SortingHeaderButton } from '@/components/ui/DataTable/SortingHeaderButton'
import { SkillActionsMenu } from './SkillActionsMenu'
import { getVisibilityLabel, SkillSummary } from './platformAgentSkillsTableUtils'

const scopeMultiSelectFilter = (row: Row<SkillSummary>, _columnId: string, filterValue: unknown) => {
  if (!Array.isArray(filterValue) || filterValue.length === 0) return true

  const skill = row.original
  const selected = filterValue as string[]

  return selected.some((value) => {
    if (value === 'platform') return skill.isPlatform
    if (value === 'public') return skill.isPublic
    if (value === 'owned') return skill.canEdit && !skill.isPlatform
    if (value === 'shared') return !skill.canEdit && !skill.isPlatform
    return false
  })
}

export const skillColumns: ColumnDef<SkillSummary>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: ({ column }) => <SortingHeaderButton column={column}>Skill</SortingHeaderButton>,
    cell: ({ row }) => (
      <div className="flex flex-col gap-y-1">
        <span className="text-base">{row.original.name}</span>
        <span className="text-xs font-light leading-[1.1] text-muted-foreground">
          {row.original.description || 'No description'}
        </span>
      </div>
    ),
    sortingFn: (rowA, rowB) => (rowA.original.name || '').localeCompare(rowB.original.name || ''),
  },
  {
    id: 'key',
    accessorKey: 'key',
    header: ({ column }) => <SortingHeaderButton column={column}>Key</SortingHeaderButton>,
    cell: ({ row }) => (
      <Badge variant="outline" className="max-w-full truncate font-normal">
        {row.original.key}
      </Badge>
    ),
    sortingFn: (rowA, rowB) => (rowA.original.key || '').localeCompare(rowB.original.key || ''),
  },
  {
    id: 'scope',
    accessorFn: (skill) => getVisibilityLabel(skill),
    header: ({ column }) => <SortingHeaderButton column={column}>Scope</SortingHeaderButton>,
    cell: ({ row }) => {
      const skill = row.original
      return (
        <div className="flex flex-wrap gap-1">
          {skill.isPlatform ? <Badge variant="secondary">Platform</Badge> : null}
          <Badge variant={skill.isPublic ? 'default' : 'outline'}>{skill.isPublic ? 'Public' : 'Private'}</Badge>
          {skill.canEdit ? <Badge variant="outline">Editable</Badge> : null}
        </div>
      )
    },
    filterFn: scopeMultiSelectFilter,
    sortingFn: (rowA, rowB) => getVisibilityLabel(rowA.original).localeCompare(getVisibilityLabel(rowB.original)),
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: ({ column }) => <SortingHeaderButton column={column}>Status</SortingHeaderButton>,
    cell: ({ row }) => (
      <Badge variant="outline" className="font-normal capitalize">
        {row.original.status || 'unknown'}
      </Badge>
    ),
    sortingFn: (rowA, rowB) => (rowA.original.status || '').localeCompare(rowB.original.status || ''),
  },
  {
    id: 'source',
    accessorKey: 'source',
    header: ({ column }) => <SortingHeaderButton column={column}>Source</SortingHeaderButton>,
    cell: ({ row }) => (
      <span className="text-sm capitalize text-muted-foreground">{row.original.source || 'manual'}</span>
    ),
    sortingFn: (rowA, rowB) => (rowA.original.source || '').localeCompare(rowB.original.source || ''),
  },
  {
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <div className="flex justify-end pr-5">
        <SkillActionsMenu skill={row.original} />
      </div>
    ),
  },
]
